import type { StudioRuleEntry } from './useExistingStudioRules.ts';

/** Trigger fired when a page's status changes; carries the target statuses in `value.pageStatuses`. */
const PAGE_STATUS_CHANGED_TRIGGER_TYPE = 'confluence.page-status-changed.trigger';
/** Trigger fired when a page is published. */
const PAGE_PUBLISHED_TRIGGER_TYPE = 'confluence.page-published.trigger';
/** Action that runs a Rovo agent (the Content Reviewer agent, for our purposes). */
const INVOKE_AGENT_ACTION_TYPE = 'rovo.invoke-agent.action';
/** Action that sends a templated email (the meeting-notes recap). */
const SEND_TEMPLATED_EMAIL_ACTION_TYPE = 'action.send-templated-email';
/** The page status that triggers the Content Reviewer nudge. */
const READY_FOR_REVIEW = 'ready for review';
/** Text a meeting-notes rule's name/description is expected to contain. */
const MEETING_NOTES_TEXT = 'meeting notes';

const equalsIgnoreCase = (value: string | undefined, expectedLower: string): boolean =>
	typeof value === 'string' && value.toLowerCase() === expectedLower;

const isRule = (rule: StudioRuleEntry | null | undefined): rule is StudioRuleEntry =>
	rule != null && typeof rule === 'object';

const hasComponentOfType = (rule: StudioRuleEntry, typeLower: string): boolean =>
	Array.isArray(rule.components) &&
	rule.components.some((component) => equalsIgnoreCase(component?.type, typeLower));

/** True when the rule's trigger is of the given type. */
const hasTriggerType = (rule: StudioRuleEntry, typeLower: string): boolean =>
	equalsIgnoreCase(rule.trigger?.type, typeLower);

/** True when the rule is triggered by a page-status change to "Ready for review". */
const triggersOnReadyForReview = (rule: StudioRuleEntry): boolean => {
	if (!hasTriggerType(rule, PAGE_STATUS_CHANGED_TRIGGER_TYPE)) {
		return false;
	}
	const statuses = rule.trigger?.value?.pageStatuses;
	return (
		Array.isArray(statuses) &&
		statuses.some((status) => equalsIgnoreCase(status?.label, READY_FOR_REVIEW))
	);
};

/**
 * Returns `true` when any rule is a meeting-notes automation: it is triggered by a page publish, its
 * name/description contains "meeting notes", AND it sends a templated email. Rule state
 * (enabled/disabled) is ignored.
 */
export const existingMeetingNotesRuleExists = (rules: StudioRuleEntry[]): boolean =>
	rules.some(
		(rule) =>
			isRule(rule) &&
			hasTriggerType(rule, PAGE_PUBLISHED_TRIGGER_TYPE) &&
			`${rule.name ?? ''} ${rule.description ?? ''}`.toLowerCase().includes(MEETING_NOTES_TEXT) &&
			hasComponentOfType(rule, SEND_TEMPLATED_EMAIL_ACTION_TYPE),
	);

/**
 * Returns `true` when any rule is a content-reviewer automation: it is triggered by a page-status
 * change to "Ready for review" AND it runs the Rovo agent (invoke-agent) action. Rule state
 * (enabled/disabled) is ignored.
 */
export const existingContentReviewerRuleExists = (rules: StudioRuleEntry[]): boolean =>
	rules.some(
		(rule) =>
			isRule(rule) &&
			triggersOnReadyForReview(rule) &&
			hasComponentOfType(rule, INVOKE_AGENT_ACTION_TYPE),
	);
